/**
 * Mentions and replies - the pure rules and words (docs/plans/crm-report-builder-search-collab-plan.md
 * A3.3 to A3.5, B3.2, B3.4 and B3.5). Type imports only, no api import chain, so the composer, the
 * timeline, the Today lane, the Mentions page, the corner card and their tests use it without the
 * network layer.
 *
 * ONE rule decides what counts as a mention, so what the picker inserts is exactly what is kept at
 * Post (extractMentions) and what is highlighted on the timeline (mentionSegments):
 *   - a mention is "@" followed by a teammate's name, and the "@" sits at the start of the text or
 *     right after whitespace or an opening bracket or quote, so "dana@wcr.com" is never one;
 *   - the name ends at the end of the text or before a character that is not a letter or a digit
 *     ("@Dana Smith's" and "@Dana Smith," name Dana Smith; "@Dana Smithers" does not);
 *   - where two names fit at one "@", the longer wins ("@Dana Smith" over a teammate named "Dana");
 *   - letter case does not matter; a name is compared with its whitespace collapsed to single
 *     spaces (normalizeMentionName), which is also how the picker inserts it.
 *
 * The picker (M-6's MentionInput) runs detectTrigger on every keystroke and caret move, lists
 * filterMentionable(users, trigger.query), and on a pick replaces the typed "@..." through
 * applyPick, recording the teammate; at Post it sends extractMentions(text, picked), the ids whose
 * "@Name" is still in the text. Every recruiter-facing word about a mention (the lane row, the corner
 * card, the composer's "we could not tell" line) is rendered here, never ad hoc in a component.
 */
import type { WorklistMentionsBucket } from '../types';
import type { MentionItem, MentionKind, MentionableUser, NoteMentionMeta } from './types';

// ---------------------------------------------------------------------------------------------
// The contract's numbers (the backend's routes and service carry the same values)
// ---------------------------------------------------------------------------------------------

/** The snippet the server sends with each mention: the note's first 200 characters (B3.2). */
export const MENTION_SNIPPET_MAX = 200;

/** At most 20 people named in one note: the note routes refuse more (B12.8 N-4 and X-2). */
export const MENTIONS_PER_NOTE_MAX = 20;

/** GET /mentions answers at most 50 rows a page (B3.3 `limit` 1..50). */
export const MENTION_LIST_LIMIT_MAX = 50;

/** POST /mentions/read takes at most 50 ids at once (B3.3); api.ts sends more in batches. */
export const MENTION_READ_IDS_MAX = 50;

/** The longest text after "@" that still counts as a search for a teammate. */
export const MENTION_QUERY_MAX = 50;

/** How many teammates the picker lists at once. */
export const MENTION_PICKER_LIMIT = 8;

/** The query parameter that opens a lead on one note: /crm/contacts/<id>?note=<note id> (B3.4). */
export const MENTION_NOTE_PARAM = 'note';

/** The corner card's id prefix: `mention:<mention id>`, stable across refreshes. */
export const MENTION_CARD_PREFIX = 'mention:';

// ---------------------------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isSpace = (ch: string | undefined): boolean => typeof ch === 'string' && /\s/.test(ch);

/** A letter (any cased script) or a digit: what a name is made of, and what a name may not run into. */
const isNameChar = (ch: string | undefined): boolean =>
  typeof ch === 'string' && ch !== '' && (/[0-9]/.test(ch) || ch.toLowerCase() !== ch.toUpperCase());

/** What may sit right before a mention's "@" besides whitespace: "(@Dana Smith)". */
const OPENERS = new Set(['(', '[', '{', '"', "'"]);

/** Characters no name is typed with: typing one after "@..." ends the search ("@Dana, can you"). */
const QUERY_STOPS = new Set([',', ';', ':', '!', '?', '(', ')', '[', ']', '{', '}', '"', '<', '>']);

/** Joiners that belong to the word the caret sits in when a letter or digit follows ("dana@wcr.com"). */
const WORD_JOINERS = new Set(['.', '-', "'", '_', '@']);

/** An "@" at i can start a mention: at the start of the text, or after whitespace or an opener. */
function canStartAt(text: string, i: number): boolean {
  if (text[i] !== '@') return false;
  if (i === 0) return true;
  const prev = text[i - 1];
  return isSpace(prev) || OPENERS.has(prev);
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/** "Now" as a Date or epoch milliseconds; the current time when absent. */
export type MentionNow = Date | number;

const nowMs = (now: MentionNow | undefined): number =>
  now === undefined ? Date.now() : typeof now === 'number' ? now : now.getTime();

const timeOf = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Sep 26", or "Sep 26, 2025" outside the current year (the notes words, noteLogic.ts). */
function dayWords(t: number, now: number): string {
  const d = new Date(t);
  const day = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === new Date(now).getFullYear() ? day : `${day}, ${d.getFullYear()}`;
}

/** "just now", "2 min ago", "3 h ago", "2 d ago", then the day ("Sep 26"); '' when unknown. */
function agoWords(iso: string | null | undefined, now: number): string {
  const t = timeOf(iso);
  if (t === null) return '';
  const diff = now - t;
  if (diff < 60e3) return 'just now';
  const minutes = Math.floor(diff / 60e3);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(diff / 3600e3);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(diff / 86400e3);
  if (days < 7) return `${days} d ago`;
  return dayWords(t, now);
}

const firstName = (full: string): string => full.trim().split(/\s+/)[0] || '';

/** "Dana", "Dana and Luke", "Dana, Luke and Pat". */
function joinNames(parts: string[]): string {
  if (parts.length <= 1) return parts[0] || '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

// ---------------------------------------------------------------------------------------------
// Names, ids and the rows the server sends
// ---------------------------------------------------------------------------------------------

/** A name as the picker inserts it and the matcher compares it: trimmed, whitespace runs as one space. */
export function normalizeMentionName(name: unknown): string {
  return typeof name === 'string' ? name.trim().replace(/\s+/g, ' ') : '';
}

/** The UUID-shaped strings of a list, trimmed, each once, in order (junk is dropped, never sent). */
export function cleanIds(ids: unknown): string[] {
  if (!Array.isArray(ids)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of ids) {
    if (typeof raw !== 'string') continue;
    const id = raw.trim();
    const key = id.toLowerCase();
    if (!UUID_RE.test(id) || seen.has(key)) continue;
    seen.add(key);
    out.push(id);
  }
  return out;
}

/**
 * Teammates as the picker and the matcher use them: a string id, a normalized non-empty name, each
 * id once, in the order given (the server sorts by name). Reads GET /mentionable's `users` and any
 * picked list.
 */
export function mentionableUsersOf(raw: unknown): MentionableUser[] {
  if (!Array.isArray(raw)) return [];
  const out: MentionableUser[] = [];
  const seen = new Set<string>();
  for (const u of raw) {
    if (!u || typeof u !== 'object') continue;
    const id = (u as { id?: unknown }).id;
    const name = normalizeMentionName((u as { name?: unknown }).name);
    if (typeof id !== 'string' || !id || !name || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name });
  }
  return out;
}

function cleanMetas(raw: unknown): NoteMentionMeta[] {
  if (!Array.isArray(raw)) return [];
  const out: NoteMentionMeta[] = [];
  const seen = new Set<string>();
  for (const m of raw) {
    if (!m || typeof m !== 'object') continue;
    const id = (m as { user_id?: unknown }).user_id;
    const name = normalizeMentionName((m as { name?: unknown }).name);
    if (typeof id !== 'string' || !id || !name || seen.has(id)) continue;
    seen.add(id);
    out.push({ user_id: id, name });
  }
  return out;
}

/**
 * A note's metadata.mentions (B3.2: `[{ user_id, name }]`, the names at writing time), read
 * defensively: entries without an id or a name are dropped, each person once. [] for any other
 * metadata.
 */
export function noteMentionsOf(metadata: unknown): NoteMentionMeta[] {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return [];
  return cleanMetas((metadata as { mentions?: unknown }).mentions);
}

/**
 * The picked list an editor of an existing note starts from (the people the note already names), so
 * an edit that keeps "@Dana Smith" keeps Dana and one that removes it withdraws her unread mention.
 */
export function pickedFromNote(metadata: unknown): MentionableUser[] {
  return noteMentionsOf(metadata).map((m) => ({ id: m.user_id, name: m.name }));
}

/**
 * One mention row as the server sends it (GET /mentions, and the worklist's buckets.mentions.items),
 * read defensively into the MentionItem shape; null when it lacks the ids a row needs to open (its
 * own id, the note's id, the lead's id).
 */
export function mentionItemOf(raw: unknown): MentionItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = str(r.id);
  const note = r.note && typeof r.note === 'object' ? (r.note as Record<string, unknown>) : null;
  const noteId = note ? str(note.id) : null;
  if (!id || !note || !noteId) return null;
  const contact = r.contact && typeof r.contact === 'object' ? (r.contact as Record<string, unknown>) : null;
  const contactId = str(note.contact_id) ?? (contact ? str(contact.id) : null);
  if (!contactId) return null;
  return {
    id,
    kind: r.kind === 'reply' ? 'reply' : 'mention',
    created_at: str(r.created_at) ?? '',
    read_at: str(r.read_at),
    note: {
      id: noteId,
      contact_id: contactId,
      snippet: typeof note.snippet === 'string' ? note.snippet : '',
      deleted: note.deleted === true,
      occurred_at: str(note.occurred_at) ?? '',
    },
    contact: {
      id: (contact ? str(contact.id) : null) ?? contactId,
      full_name: contact ? str(contact.full_name) : null,
    },
    author_user_id: str(r.author_user_id),
  };
}

/**
 * The `mentions` bucket of GET /worklist (and of `?counts=true`, where it carries `newest`), or null.
 * Null means "this api does not serve mentions" (an older deploy, or a malformed body): the lane and
 * the card hide on null, so a missing bucket is never read as "no mentions" (the outreach rule,
 * outreach/outreachSurfaces.ts parseWorklistOutreach).
 */
export function parseMentionsBucket(raw: unknown): WorklistMentionsBucket | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.count !== 'number' || !Number.isFinite(o.count)) return null;
  const bucket: WorklistMentionsBucket = { count: Math.max(0, Math.floor(o.count)) };
  if (Array.isArray(o.items)) {
    const items: MentionItem[] = [];
    for (const row of o.items) {
      const item = mentionItemOf(row);
      if (item) items.push(item);
    }
    bucket.items = items;
  }
  if (o.newest === null) {
    bucket.newest = null;
  } else if (o.newest && typeof o.newest === 'object') {
    const n = o.newest as Record<string, unknown>;
    const newestId = str(n.id);
    const newestAt = str(n.created_at);
    if (newestId && newestAt) bucket.newest = { id: newestId, created_at: newestAt };
  }
  return bucket;
}

// ---------------------------------------------------------------------------------------------
// Where a mention opens (B3.4: the card's Open, a lane row, the Mentions page)
// ---------------------------------------------------------------------------------------------

/** /crm/contacts/<contact id>?note=<note id>; without a note id, the lead alone. */
export function noteHref(contactId: string, noteId?: string | null): string {
  const base = `/crm/contacts/${encodeURIComponent(contactId)}`;
  return noteId ? `${base}?${MENTION_NOTE_PARAM}=${encodeURIComponent(noteId)}` : base;
}

/** Where a mention opens: its lead, scrolled to its note. The Mentions page when the row has no lead. */
export function mentionHref(item: Pick<MentionItem, 'note' | 'contact'> | null | undefined): string {
  const contactId = item?.note?.contact_id || item?.contact?.id || '';
  if (!contactId) return '/crm/mentions';
  return noteHref(contactId, item?.note?.id || null);
}

/**
 * The note id of `?note=<id>` in a location's search string (ContactDetail scrolls to and highlights
 * that note); null when absent or not a UUID.
 */
export function noteParamOf(search: string | null | undefined): string | null {
  if (typeof search !== 'string' || !search) return null;
  let value: string | null = null;
  try {
    value = new URLSearchParams(search).get(MENTION_NOTE_PARAM);
  } catch {
    return null;
  }
  const id = typeof value === 'string' ? value.trim() : '';
  return UUID_RE.test(id) ? id : null;
}

// ---------------------------------------------------------------------------------------------
// The matcher: every "@Name" of a list of names in a text
// ---------------------------------------------------------------------------------------------

interface MentionHit {
  /** The "@". */
  start: number;
  /** Just past the name. */
  end: number;
  /** The matched name, lower-cased. */
  key: string;
}

/** Every mention token of `names` in the text, left to right; at each "@" the longest name that fits. */
function scanMentions(text: string, names: string[]): MentionHit[] {
  const hits: MentionHit[] = [];
  if (!text || !names.length) return hits;
  const entries: Array<{ key: string; len: number }> = [];
  const seen = new Set<string>();
  for (const name of names) {
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    entries.push({ key, len: name.length });
  }
  entries.sort((a, b) => b.len - a.len);
  for (let i = 0; i < text.length; i += 1) {
    if (!canStartAt(text, i)) continue;
    for (const e of entries) {
      const end = i + 1 + e.len;
      if (end > text.length) continue;
      if (text.slice(i + 1, end).toLowerCase() !== e.key) continue;
      if (isNameChar(text[end])) continue;
      hits.push({ start: i, end, key: e.key });
      i = end - 1;
      break;
    }
  }
  return hits;
}

// ---------------------------------------------------------------------------------------------
// The composer: the trigger, the pick, and what is sent at Post (B3.5)
// ---------------------------------------------------------------------------------------------

/** An open "@" search: the "@" at `start`, the caret at `end`, and what was typed between them. */
export interface MentionTrigger {
  /** Index of the "@". */
  start: number;
  /** The caret: the end of what has been typed after the "@". */
  end: number;
  /** What has been typed after the "@"; may hold single spaces ("Dana Sm"). */
  query: string;
}

/**
 * The open "@" search at the caret, or null. The "@" must start a mention (the start of the text,
 * or after whitespace or an opening bracket or quote); what follows it up to the caret is the query:
 * at most MENTION_QUERY_MAX characters, on one line, not starting with a space, with no two spaces
 * in a row and none of , ; : ! ? ( ) [ ] { } " < >. "@" alone opens the whole list.
 *
 * `picked` (the teammates picked so far) closes the search once the query is a picked name, so the
 * list does not open again on "@Dana Smith " right after the pick.
 *
 * A trigger may match nobody ("@Dana Smith called her back" still reads as a search until the
 * line ends): the picker shows only when filterMentionable finds someone.
 */
export function detectTrigger(
  text: string | null | undefined,
  caret: number | null | undefined,
  picked?: ReadonlyArray<MentionableUser> | null,
): MentionTrigger | null {
  if (typeof text !== 'string' || typeof caret !== 'number' || !Number.isInteger(caret)) return null;
  if (caret < 1 || caret > text.length) return null;
  const floor = Math.max(0, caret - 1 - MENTION_QUERY_MAX);
  for (let i = caret - 1; i >= floor; i -= 1) {
    const ch = text[i];
    if (ch === '\n' || ch === '\r' || QUERY_STOPS.has(ch)) return null;
    if (!canStartAt(text, i)) continue;
    const query = text.slice(i + 1, caret);
    if (/^\s/.test(query) || /\s\s/.test(query)) return null;
    const typed = normalizeMentionName(query).toLowerCase();
    if (typed && mentionableUsersOf(picked).some((u) => u.name.toLowerCase() === typed)) return null;
    return { start: i, end: caret, query };
  }
  return null;
}

/** The result of a pick: the new text, where the caret goes, and the teammate to record. */
export interface MentionPick {
  /** The text with "@Full Name" in place of the "@" and what was typed after it. */
  text: string;
  /** Just after the inserted name and the space that follows it (before punctuation that follows). */
  caret: number;
  /** The teammate picked, name normalized: add it to the composer's picked list. */
  user: MentionableUser;
}

/**
 * Put "@Full Name " in place of the trigger's "@query". The rest of the word the caret sits in goes
 * too ("@Da|na" becomes "@Dana Smith "). A space follows the name unless one is already there, or
 * punctuation follows ("@Dana Smith, can you"). Null when the trigger no longer fits the text (it
 * was detected in another text) or the teammate has no id or name.
 */
export function applyPick(
  text: string | null | undefined,
  trigger: MentionTrigger | null | undefined,
  user: MentionableUser | null | undefined,
): MentionPick | null {
  if (typeof text !== 'string' || !trigger) return null;
  const { start, end } = trigger;
  if (!Number.isInteger(start) || !Number.isInteger(end)) return null;
  if (start < 0 || end <= start || end > text.length || text[start] !== '@') return null;
  if (text.slice(start + 1, end) !== trigger.query) return null;
  const id = user && typeof user.id === 'string' ? user.id : '';
  const name = normalizeMentionName(user?.name);
  if (!id || !name) return null;

  let stop = end;
  while (stop < text.length) {
    const ch = text[stop];
    if (isNameChar(ch) || (WORD_JOINERS.has(ch) && isNameChar(text[stop + 1]))) {
      stop += 1;
      continue;
    }
    break;
  }
  const before = text.slice(0, start);
  const after = text.slice(stop);
  const token = `@${name}`;
  if (after.startsWith(' ')) {
    return { text: `${before}${token}${after}`, caret: start + token.length + 1, user: { id, name } };
  }
  if (after === '' || isSpace(after[0])) {
    return { text: `${before}${token} ${after}`, caret: start + token.length + 1, user: { id, name } };
  }
  return { text: `${before}${token}${after}`, caret: start + token.length, user: { id, name } };
}

/**
 * The ids to send as `mentions` at Post: the picked teammates whose "@Name" is still in the text
 * (plan B3.5), in the order they first appear, each once. A name edited out, or changed ("@Dana
 * Smit"), drops its teammate; a name typed by hand without picking is not a mention. Two picked
 * teammates with the same name are both kept while that name is in the text.
 */
export function extractMentions(
  text: string | null | undefined,
  picked: ReadonlyArray<MentionableUser> | null | undefined,
): string[] {
  const body = typeof text === 'string' ? text : '';
  const users = mentionableUsersOf(picked);
  if (!body || !users.length) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const hit of scanMentions(body, users.map((u) => u.name))) {
    for (const u of users) {
      if (u.name.toLowerCase() !== hit.key || seen.has(u.id)) continue;
      seen.add(u.id);
      out.push(u.id);
    }
  }
  return out;
}

export interface MentionFilterOptions {
  /** Never offered: the viewer (mentioning yourself tells nobody, A3.5). */
  excludeIds?: ReadonlyArray<string | null | undefined>;
  /** At most this many; MENTION_PICKER_LIMIT when absent. */
  limit?: number;
}

/**
 * The picker's list for a query: teammates whose name starts with it, then those with a later word
 * that starts with it ("smi" finds Dana Smith; "dana sm" works across words), each group in the
 * server's order (by name). Letter case does not matter. An empty query lists everyone.
 */
export function filterMentionable(
  users: ReadonlyArray<MentionableUser> | null | undefined,
  query: string | null | undefined,
  opts: MentionFilterOptions = {},
): MentionableUser[] {
  const exclude = new Set((opts.excludeIds || []).filter((x): x is string => typeof x === 'string'));
  const limit = typeof opts.limit === 'number' && opts.limit >= 1 ? Math.floor(opts.limit) : MENTION_PICKER_LIMIT;
  const list = mentionableUsersOf(users).filter((u) => !exclude.has(u.id));
  const q = normalizeMentionName(query).toLowerCase();
  if (!q) return list.slice(0, limit);
  const ranked: Array<{ user: MentionableUser; score: number; index: number }> = [];
  list.forEach((user, index) => {
    const name = user.name.toLowerCase();
    let score = -1;
    if (name.startsWith(q)) {
      score = 0;
    } else {
      for (let p = name.indexOf(' '); p !== -1; p = name.indexOf(' ', p + 1)) {
        if (name.startsWith(q, p + 1)) {
          score = 1;
          break;
        }
      }
    }
    if (score >= 0) ranked.push({ user, score, index });
  });
  ranked.sort((a, b) => a.score - b.score || a.index - b.index);
  return ranked.slice(0, limit).map((r) => r.user);
}

/** A picked teammate, as notifyFailedWords names them. */
type NamedUser = { name?: string | null };

/**
 * The composer's line when the note saved but its notifications did not (`mentions: { failed: true }`,
 * B3.2): "Saved; we could not tell Dana. Mention them again in a reply." First names, or full names
 * when two of them share a first name.
 */
export function notifyFailedWords(users: ReadonlyArray<NamedUser> | null | undefined): string {
  const names: string[] = [];
  for (const u of users || []) {
    const name = normalizeMentionName(u?.name);
    if (name && !names.some((n) => n.toLowerCase() === name.toLowerCase())) names.push(name);
  }
  if (!names.length) return 'Saved; we could not tell your teammates. Mention them again in a reply.';
  const firsts = names.map(firstName);
  const clash = new Set(firsts.map((f) => f.toLowerCase())).size !== firsts.length;
  return `Saved; we could not tell ${joinNames(clash ? names : firsts)}. Mention them again in a reply.`;
}

// ---------------------------------------------------------------------------------------------
// The timeline: a note's body with its mentions highlighted (B3.5 MentionText)
// ---------------------------------------------------------------------------------------------

/** A run of a note's body: plain text, or one "@Name" of a person the note names. */
export type MentionSegment =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; text: string; user_id: string; name: string };

/**
 * A note's body cut into plain runs and highlighted mentions, by the note's metadata.mentions (the
 * names at writing time). Joining every segment's text gives the body back exactly; a mention's
 * text is the body's own "@Name" (its case kept). A body without mentions is one text segment; an
 * empty body is no segment.
 */
export function mentionSegments(
  body: string | null | undefined,
  mentions: ReadonlyArray<NoteMentionMeta> | null | undefined,
): MentionSegment[] {
  const text = typeof body === 'string' ? body : '';
  if (!text) return [];
  const metas = cleanMetas(mentions);
  const byKey = new Map<string, NoteMentionMeta>();
  for (const m of metas) {
    const key = m.name.toLowerCase();
    if (!byKey.has(key)) byKey.set(key, m);
  }
  const out: MentionSegment[] = [];
  let at = 0;
  for (const hit of scanMentions(text, metas.map((m) => m.name))) {
    const meta = byKey.get(hit.key);
    if (!meta) continue;
    if (hit.start > at) out.push({ kind: 'text', text: text.slice(at, hit.start) });
    out.push({ kind: 'mention', text: text.slice(hit.start, hit.end), user_id: meta.user_id, name: meta.name });
    at = hit.end;
  }
  if (at < text.length) out.push({ kind: 'text', text: text.slice(at) });
  return out;
}

// ---------------------------------------------------------------------------------------------
// The words of a mention: the Today lane and the Mentions page, and the corner card (B3.4)
// ---------------------------------------------------------------------------------------------

export interface MentionWordsOptions {
  /** A user id to a display name (useTeam().byId, or the mentionable list). */
  nameOf?: (userId: string) => string | null | undefined;
}

/** The author's first name ("Luke"), else "A teammate". */
function authorWords(item: Pick<MentionItem, 'author_user_id'> | null | undefined, opts: MentionWordsOptions): string {
  const id = item?.author_user_id;
  if (id && opts.nameOf) {
    const name = opts.nameOf(id);
    if (typeof name === 'string' && name.trim()) return firstName(name);
  }
  return 'A teammate';
}

const leadName = (item: Pick<MentionItem, 'contact'> | null | undefined): string =>
  (typeof item?.contact?.full_name === 'string' ? item.contact.full_name.trim() : '');

/** The snippet's first non-blank line, whitespace collapsed; "..." when the server cut it at 200. */
function firstLineWords(snippet: unknown): string {
  const text = typeof snippet === 'string' ? snippet : '';
  const lines = text.split(/\r?\n/);
  const index = lines.findIndex((l) => l.trim() !== '');
  if (index === -1) return '';
  const line = lines[index].trim().replace(/\s+/g, ' ');
  const runsToTheEnd = lines.slice(index + 1).every((l) => l.trim() === '');
  return runsToTheEnd && text.length >= MENTION_SNIPPET_MAX ? `${line}...` : line;
}

/** The whole snippet on one line; "..." when the server cut it at 200. */
function snippetWords(snippet: unknown): string {
  const text = typeof snippet === 'string' ? snippet : '';
  const flat = text.replace(/\s+/g, ' ').trim();
  if (!flat) return '';
  return text.length >= MENTION_SNIPPET_MAX ? `${flat}...` : flat;
}

/** One row of the Today lane or the Mentions page, in words. */
export interface MentionLaneWords {
  /** The row's link: the lead, opened on the note (mentionHref). */
  href: string;
  /** The lead's name; "Unknown lead" when the server sent none (the Today rows' word). */
  lead: string;
  /** The row's chip: "Mention" or "Reply". */
  kindLabel: string;
  /** "Luke mentioned you", or "Dana replied to your note". */
  headline: string;
  /** The note's first line ("..." when cut); "This note was deleted" once it is deleted. */
  line: string;
  /** When the mention was made: "5 min ago", "Sep 26"; '' when unknown. */
  when: string;
  /** Not read yet (the lane lists only these; the Mentions page shows both). */
  unread: boolean;
  /** The note was deleted after it was read (an unread one is withdrawn instead, A3.5). */
  deleted: boolean;
}

/**
 * A mention as a row of the Today lane or the Mentions page: "Jane Smith" [Mention] "Luke mentioned
 * you" / "Checked her records, @Dana Smith can you call her back Tuesday?" / "5 min ago".
 */
export function laneWords(item: MentionItem, now?: MentionNow, opts: MentionWordsOptions = {}): MentionLaneWords {
  const reply = item?.kind === 'reply';
  const who = authorWords(item, opts);
  const deleted = item?.note?.deleted === true;
  return {
    href: mentionHref(item),
    lead: leadName(item) || 'Unknown lead',
    kindLabel: reply ? 'Reply' : 'Mention',
    headline: reply ? `${who} replied to your note` : `${who} mentioned you`,
    line: deleted ? 'This note was deleted' : firstLineWords(item?.note?.snippet),
    when: agoWords(item?.created_at, nowMs(now)),
    unread: !item?.read_at,
    deleted,
  };
}

/** The corner card of one unread mention (the overlay stack's `mention` kind, B3.4). */
export interface MentionCard {
  /** `mention:<mention id>`: stable, so the overlay queue keeps the card's served time. */
  id: string;
  /** crm.note_mentions.id: what markMentionsRead({ ids }) takes when the card is opened. */
  mentionId: string;
  kind: MentionKind;
  contactId: string;
  noteId: string;
  /** Open: /crm/contacts/<contact id>?note=<note id>. */
  href: string;
  /** "Luke mentioned you on Jane Smith", or "Dana replied to your note on Jane Smith". */
  line: string;
  /** The note's words on one line ("..." when cut at 200); '' for a deleted note. */
  snippet: string;
  /** When the mention was made (ISO), for the queue's order; null when unknown. */
  createdAt: string | null;
}

/**
 * The corner card for a mention: "Luke mentioned you on Jane Smith" + the snippet + Open. Null when
 * the row lacks the ids the card needs to open. No word of it comes from anywhere but the row and
 * the name lookup.
 */
export function cardFromMention(
  item: MentionItem | null | undefined,
  opts: MentionWordsOptions = {},
): MentionCard | null {
  if (!item || typeof item.id !== 'string' || !item.id) return null;
  const noteId = item.note?.id || '';
  const contactId = item.note?.contact_id || item.contact?.id || '';
  if (!noteId || !contactId) return null;
  const reply = item.kind === 'reply';
  const who = authorWords(item, opts);
  const lead = leadName(item);
  const where = lead ? `on ${lead}` : 'on a lead';
  return {
    id: `${MENTION_CARD_PREFIX}${item.id}`,
    mentionId: item.id,
    kind: reply ? 'reply' : 'mention',
    contactId,
    noteId,
    href: noteHref(contactId, noteId),
    line: reply ? `${who} replied to your note ${where}` : `${who} mentioned you ${where}`,
    snippet: item.note?.deleted === true ? '' : snippetWords(item.note?.snippet),
    createdAt: typeof item.created_at === 'string' && item.created_at ? item.created_at : null,
  };
}
